import {
  addMicrobialAssessment,
  deleteMicrobialAssessment,
  HTZ_WORK_ORDER_NAMESPACE,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useInspectionContext } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/inspection-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Plus, PlusCircle, RefreshCw, Search, Trash2 } from "lucide-react";
import { H4 } from "@/shared/components/typography";
import { v4 } from "uuid";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Kind } from "@/services/backend/samples/types/kind";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { EntityId } from "@/shared/types/entity-id";
import {
  activateAirMicrobialSetSample,
  AirMicrobialSample,
  airMicrobialSampleIsActivated,
  AirMicrobialSampleSet,
  deactivateAirMicrobialSetSample,
  newAirMicrobialSampleSet,
  updateAirMicrobialSetCaso,
  updateAirMicrobialSetComment,
  updateAirMicrobialSetDg18,
  updateAirMicrobialSetLab,
  updateAirMicrobialSetMalt,
  updateAirMicrobialSetNumber,
  updateCreatedAtInAirSet,
  updateDeviceIdInSet,
  updateSamplingVolumeInSet,
} from "@/services/backend/samples/types/air-microbial-sample";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { CheckedState } from "@radix-ui/react-checkbox";
import {
  Sample,
  updateLocation,
  updateSampleComment,
  updateSampleCreatedAt,
  updateSampleLab,
  updateSampleNumber,
  updateSampleTakers,
  updateSpot,
} from "@/services/backend/samples/types/sample";
import { Textarea } from "@/shared/components/ui/textarea";
import { DateStringPicker } from "@/shared/components/date-picker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  activateSurfaceMicrobialSetSample,
  deactivateSurfaceMicrobialSetSample,
  newSurfaceMicrobialSampleSet,
  SurfaceMicrobialSample,
  surfaceMicrobialSampleIsActivated,
  SurfaceMicrobialSampleSet,
  updateCreatedAtInSurfaceSet,
  updateSurfaceMicrobialSetCaso,
  updateSurfaceMicrobialSetComment,
  updateSurfaceMicrobialSetDg18,
  updateSurfaceMicrobialSetLab,
  updateSurfaceMicrobialSetMalt,
  updateSurfaceMicrobialSetNumber,
} from "@/services/backend/samples/types/surface-microbial-sample";
import {
  DustDensitySample,
  newDustDensitySample,
} from "@/services/backend/samples/types/dust-density-sample";
import {
  newColdWaterSample,
  newWarmWaterSample,
  updateTemperature,
  WaterSample,
} from "@/services/backend/samples/types/water-sample";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  useGetAirMicrobialSampleSet,
  usePutAirMicrobialSampleSet,
  useListAirMicrobialSampleSets,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-air-microbial-sample-set-offline";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PutRequest } from "@/services/backend/samples/api/air-microbial-api";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  useGetSurfaceMicrobialSampleSet,
  usePutSurfaceMicrobialSampleSet,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-surface-microbial-sample-set-offline";
import {
  useGetDustDensitySample,
  usePutDustDensitySample,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-dust-density-sample-offline";
import {
  useGetWaterSample,
  usePutWaterSample,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-water-sample-offline";
import { useGetLaboratories } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-laboratories-offline";
import { useGetDevices } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-devices-offline";
import {
  OfflineState,
  useOfflineContext,
} from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/offline-context";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { TextInput } from "@/shared/components/form/text-input";
import { BooleanSelect } from "@/shared/components/form/boolean-select";
import { useGetAirHandlingUnitData } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-air-handling-unit-data-offline";
import { TableCard } from "@/shared/components/tables/table-card";

export function MicrobialInspection({ workOrder }: { workOrder: WorkOrder }) {
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const { activeComponent, position } = useInspectionContext();

  return (
    <ScrollArea className="h-[65vh] space-y-2">
      <MicrobialAssessmentTable
        workOrder={workOrder}
        selectedAssessmentId={assessmentId}
        onAssessmentSelectedId={(a) => setAssessmentId(a)}
      />
      <div className="p-2 py-4">
        {position.microbialAssessments
          .filter((a) => a.componentId === activeComponent?.componentId)
          .map((assessment) => {
            if (assessmentId !== assessment.id) return null;
            return (
              <MicrobialAssessmentForm
                key={assessment.id}
                workOrder={workOrder}
                assessment={assessment}
              />
            );
          })}
      </div>
    </ScrollArea>
  );
}

export function AddMicrobialAssessmentButton({
  workOrder,
}: {
  workOrder: WorkOrder;
}) {
  const {
    activeComponent,
    position,
    onPositionChange: [putPosition, { isLoading: ppIsLoading, error: ppError }],
    disabled: ctxDisabled,
  } = useInspectionContext();

  const [
    putAirMicrobialSampleSet,
    { isLoading: amsIsLoading, error: amsError, reset: amsReset },
  ] = usePutAirMicrobialSampleSet();

  const [
    putSurfaceMicrobialSampleSet,
    { isLoading: smsIsLoading, error: smsError, reset: smsReset },
  ] = usePutSurfaceMicrobialSampleSet();

  const [
    putDustDensitySample,
    { isLoading: ddsIsLoading, error: ddsError, reset: ddsReset },
  ] = usePutDustDensitySample();

  const [
    putWaterSample,
    { isLoading: wsIsLoading, error: wsError, reset: wsReset },
  ] = usePutWaterSample();

  const isLoading =
    amsIsLoading || smsIsLoading || ddsIsLoading || wsIsLoading || ppIsLoading;

  const error = amsError || smsError || ddsError || wsError || ppError;

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      amsReset();
      smsReset();
      ddsReset();
      wsReset();
    }
  }, [amsReset, ddsReset, error, smsReset, toast, wsReset]);

  const { resetNotReadyErrorField } = useErrDocumentNotReady();
  const errorKey = `position.${position.id}.microbialAssessments`;

  const { data: airHandlingUnit } = useGetAirHandlingUnitData(
    position.ahuDataId ?? "",
  );

  const doAdd = (kind: Kind, water?: string) => () => {
    if (isLoading) {
      return;
    }

    // just a precaution, should not happen, or be available eventually
    if (!airHandlingUnit) {
      return;
    }

    // A new assessment needs a new sample or sample set depending on the kind.
    const sampleId = v4();
    const assessmentId = v4();

    switch (kind) {
      case Kind.AirMicrobial:
        putAirMicrobialSampleSet(
          newAirMicrobialSampleSet(
            sampleId,
            workOrder.id,
            HTZ_WORK_ORDER_NAMESPACE,
            airHandlingUnit.designation,
            activeComponent?.name ?? "",
          ),
        );
        break;
      case Kind.SurfaceMicrobial:
        putSurfaceMicrobialSampleSet(
          newSurfaceMicrobialSampleSet(
            sampleId,
            workOrder.id,
            HTZ_WORK_ORDER_NAMESPACE,
            airHandlingUnit.designation,
            activeComponent?.name ?? "",
          ),
        );
        break;
      case Kind.DustDensity:
        putDustDensitySample(
          newDustDensitySample(
            sampleId,
            workOrder.id,
            HTZ_WORK_ORDER_NAMESPACE,
            airHandlingUnit.designation,
            activeComponent?.name ?? "",
          ),
        );
        break;
      case Kind.Water:
        switch (water) {
          case "warm":
            putWaterSample(
              newWarmWaterSample(
                sampleId,
                workOrder.id,
                HTZ_WORK_ORDER_NAMESPACE,
                airHandlingUnit.designation,
                activeComponent?.name ?? "",
              ),
            );
            break;
          case "cold":
            putWaterSample(
              newColdWaterSample(
                sampleId,
                workOrder.id,
                HTZ_WORK_ORDER_NAMESPACE,
                airHandlingUnit.designation,
                activeComponent?.name ?? "",
              ),
            );
            break;
          default:
            throw new Error(`Unknown water kind: ${water}`);
        }
        break;
      default:
        throw new Error(`Unknown kind: ${kind}`);
    }

    putPosition(
      addMicrobialAssessment(
        position,
        activeComponent!.componentId!,
        activeComponent!.id,
        assessmentId,
        kind,
        sampleId,
      ),
    );
    resetNotReadyErrorField(errorKey);
  };

  const disabled =
    workOrder.immutable || activeComponent === null || isLoading || ctxDisabled;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button>
          <PlusCircle />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("Neue Probe hinzufügen")}</DropdownMenuLabel>
        <DropdownMenuItem onClick={doAdd(Kind.AirMicrobial)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Luftkeimprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.SurfaceMicrobial)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Oberflächenkeimprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.DustDensity)}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Staubdichtemessung")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.Water, "cold")}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Kaltwasserprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={doAdd(Kind.Water, "warm")}>
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Warmwasserprobe")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("Bestehende Luftkeimprobe")}</DropdownMenuLabel>
        <SampleSearch
          onClick={(kind, sampleId) => {
            if (isLoading) {
              return;
            }

            // just a precaution, should not happen, or be available eventually
            if (!airHandlingUnit) {
              return;
            }

            const assessmentId = v4();

            putPosition(
              addMicrobialAssessment(
                position,
                activeComponent!.componentId!,
                activeComponent!.id,
                assessmentId,
                kind,
                sampleId,
              ),
            );
            resetNotReadyErrorField(errorKey);
          }}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SampleSearch({
  onClick,
}: {
  onClick: (kind: Kind, sampleId: string) => void;
}) {
  const { data } = useListAirMicrobialSampleSets();
  const [search, setSearch] = useState("");

  if (!data) {
    return t("Keine Proben");
  }

  const samples = data.filter((s: AirMicrobialSampleSet) => {
    const lower = search.toLowerCase();
    return (
      s.number.toLowerCase().includes(lower) ||
      s.location.toLowerCase().includes(lower)
    );
  });

  return (
    <div className="grid gap-2 px-2">
      <div className="flex gap-2">
        <Input
          placeholder={t("Suchen")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button>
          <Search />
        </Button>
      </div>
      <Separator />
      <ScrollArea className="max-h-60">
        <div className="grid gap-1">
          {samples?.map((s: AirMicrobialSampleSet) => (
            <Button
              key={s.id}
              variant="ghost"
              className="justify-start font-normal has-[>svg]:px-0"
              size="sm"
              onClick={() => onClick(Kind.AirMicrobial, s.id)}
            >
              <Plus className="text-muted-foreground mr-2" />
              <span className="whitespace-nowrap">{`${s.number} - ${s.location} - ${s.spot}`}</span>
            </Button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function MicrobialAssessmentTable({
  workOrder,
  selectedAssessmentId,
  onAssessmentSelectedId,
}: {
  workOrder: WorkOrder;
  selectedAssessmentId: string | null;
  onAssessmentSelectedId: (a: string | null) => void;
}) {
  const { position, activeComponent } = useInspectionContext();

  const toggle = (assessment: MicrobialAssessment) => {
    if (assessment.id === selectedAssessmentId) {
      onAssessmentSelectedId(null);
    } else {
      onAssessmentSelectedId(assessment.id);
    }
  };

  return (
    <TableCard className="shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8">{t("Probenart")}</TableHead>
            <TableHead className="h-8">{t("")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {position.microbialAssessments
            ?.filter(
              (a: MicrobialAssessment) =>
                a.componentId === activeComponent?.componentId,
            )
            .map((assessment: MicrobialAssessment) => (
              <TableRow
                key={assessment.id}
                className={cn(
                  "cursor-pointer",
                  assessment.id === selectedAssessmentId ? "bg-accent" : "",
                )}
              >
                <TableCell className="py-1" onClick={() => toggle(assessment)}>
                  {t(assessment.kind)}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <DeleteAssessmentDialog
                    workOrder={workOrder}
                    assessmentId={assessment.id}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function DeleteAssessmentDialog({
  workOrder,
  assessmentId,
}: {
  workOrder: WorkOrder;
  assessmentId: string;
}) {
  const [open, setOpen] = useState(false);
  const { offlineState } = useOfflineContext();
  const {
    position,
    onPositionChange: [putPosition, { isLoading, error, reset, isSuccess }],
  } = useInspectionContext();

  const doDelete = () => {
    if (isLoading) return;

    // Samples are not deleted, since they
    // may be associated with a different
    // Position as well. This typically the
    // "Außenluftprobe" which is re-used,
    // across multiple air handling units.

    putPosition(deleteMicrobialAssessment(position, assessmentId));
  };

  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      reset();
    }
  }, [isSuccess, reset]);

  const disabled = workOrder.immutable || isLoading;

  // If we are offline, we cannot delete the assessment.
  // If a sample is associated with the assessment, it should not be deleted.
  // This is especially important when the sample is already in a delivery.
  // This cannot be reliably checked when offline. Online, the backend will
  // prevent deletion.
  if (offlineState !== OfflineState.ONLINE) {
    return (
      <Button size="sm" variant="destructive" className="h-8 w-8 p-0" disabled>
        <Trash2 className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={disabled}>
        <Button size="sm" variant="destructive" className="h-8 w-8 p-0">
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Beurteilung löschen?")}</DialogTitle>
          <DialogDescription>
            {t(
              "Die zugeordnete Probe wird nicht automatisch mit gelöscht und kann an anderer Stelle weiterverwendet werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={doDelete} disabled={isLoading}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MicrobialAssessmentForm({
  workOrder,
  assessment,
}: {
  workOrder: WorkOrder;
  assessment: MicrobialAssessment;
}) {
  const { immutable } = workOrder;

  switch (assessment.kind) {
    case Kind.AirMicrobial:
      return (
        <AirMicrobialSampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.SurfaceMicrobial:
      return (
        <SurfaceMicrobialSampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.DustDensity:
      return (
        <DustDensitySampleFormLoader
          id={assessment.sampleId}
          immutable={immutable}
        />
      );
    case Kind.Water:
      return (
        <WaterSampleFormLoader id={assessment.sampleId} immutable={immutable} />
      );
    default:
      return null;
  }
}

function AirMicrobialSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useGetAirMicrobialSampleSet(id!);

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.AirMicrobial)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <AirMicrobialSampleForm sampleSet={data!} immutable={immutable} />;
}

function AirMicrobialSampleForm({
  sampleSet,
  immutable,
}: {
  sampleSet: AirMicrobialSampleSet;
  immutable: boolean;
}) {
  const [request, setRequest] = useState<PutRequest>(sampleSet);
  const mutation = usePutAirMicrobialSampleSet();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    sampleSet,
    { resetOnError: false, toastError: true },
  );

  // needed to enable switching between different sample sets / assessments
  useEffect(() => {
    if (sampleSet.id !== request.id) {
      setRequest(sampleSet);
    }
  }, [request.id, sampleSet]);

  return (
    <div className="h-full">
      <H4>{t(Kind.AirMicrobial)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={request.number}
            onNumberChange={(number) =>
              setRequest(updateAirMicrobialSetNumber(request, number))
            }
            error={error}
            disabled={immutable}
          />
          <LaboratorySelect
            labId={request.labId}
            onLabIdChange={(labId) =>
              setRequest(updateAirMicrobialSetLab(request, labId))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInput
          label={t("Ort")}
          field="location"
          text={request.location}
          onTextChange={(location) =>
            setRequest(updateLocation(request, location))
          }
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInput
          label={t("Stelle")}
          field="spot"
          text={request.spot}
          onTextChange={(spot) => setRequest(updateLocation(request, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <TextInput
          label={t("Probenehmer")}
          field="sampleTakers"
          text={request.sampleTakers}
          onTextChange={(sampleTakers) =>
            setRequest(updateSampleTakers(request, sampleTakers))
          }
          errors={inputValidationErrorMessages(error, "sampleTakers")}
          disabled={immutable}
        />
        <CreatedAtInput
          createdAt={request.caso ? request.caso.createdAt : null}
          onCreatedAtChange={(createdAt) =>
            setRequest(updateCreatedAtInAirSet(request, createdAt))
          }
          error={error}
          disabled={immutable}
        />
        <DeviceSelect
          deviceId={request.caso ? request.caso.deviceId : null}
          onDeviceIdChange={(deviceId) =>
            setRequest(updateDeviceIdInSet(request, deviceId))
          }
          error={error}
          disabled={immutable}
        />
        <SamplingVolumeInput
          volume={request.caso ? request.caso.samplingVolume : 0}
          onVolumeChange={(volume) =>
            setRequest(updateSamplingVolumeInSet(request, volume))
          }
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={request.comment}
          onCommentChange={(comment) =>
            setRequest(updateAirMicrobialSetComment(request, comment))
          }
          error={error}
          disabled={immutable}
        />
        <Tabs defaultValue={GrowthMedium.CASO}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value={GrowthMedium.CASO}>
              {t(GrowthMedium.CASO)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.MALT}>
              {t(GrowthMedium.MALT)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.DG18} className="uppercase">
              {t(GrowthMedium.DG18)}
            </TabsTrigger>
          </TabsList>
          <div className="p-2">
            <TabsContent value={GrowthMedium.CASO}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.CASO}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.MALT}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.MALT}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.DG18}>
              <AirMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.DG18}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function SurfaceMicrobialSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useGetSurfaceMicrobialSampleSet(id!);

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.SurfaceMicrobial)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <SurfaceMicrobialSampleForm sampleSet={data!} immutable={immutable} />;
}

function SurfaceMicrobialSampleForm({
  sampleSet,
  immutable,
}: {
  sampleSet: SurfaceMicrobialSampleSet;
  immutable: boolean;
}) {
  const [request, setRequest] = useState(sampleSet);
  const mutation = usePutSurfaceMicrobialSampleSet();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    sampleSet,
    { resetOnError: false, toastError: true },
  );

  // needed to enable switching between different sample sets / assessments
  useEffect(() => {
    if (sampleSet.id !== request.id) {
      setRequest(sampleSet);
    }
  }, [request.id, sampleSet]);

  return (
    <div className="h-full">
      <H4>{t(Kind.SurfaceMicrobial)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={request.number}
            onNumberChange={(number) =>
              setRequest(updateSurfaceMicrobialSetNumber(sampleSet, number))
            }
            error={error}
            disabled={immutable}
          />
          <LaboratorySelect
            labId={request.labId}
            onLabIdChange={(labId) =>
              setRequest(updateSurfaceMicrobialSetLab(request, labId))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInput
          label={t("Ort")}
          field="location"
          text={request.location}
          onTextChange={(location) =>
            setRequest(updateLocation(request, location))
          }
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInput
          label={t("Stelle")}
          field="spot"
          text={request.spot}
          onTextChange={(spot) => setRequest(updateSpot(request, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <TextInput
          label={t("Probenehmer")}
          field="sampleTakers"
          text={request.sampleTakers}
          onTextChange={(sampleTakers) =>
            setRequest(updateSampleTakers(request, sampleTakers))
          }
          errors={inputValidationErrorMessages(error, "sampleTakers")}
          disabled={immutable}
        />
        <CreatedAtInput
          createdAt={request.caso ? request.caso.createdAt : null}
          onCreatedAtChange={(createdAt) =>
            setRequest(updateCreatedAtInSurfaceSet(request, createdAt))
          }
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={request.comment}
          onCommentChange={(comment) =>
            setRequest(updateSurfaceMicrobialSetComment(request, comment))
          }
          error={error}
          disabled={immutable}
        />
        <Tabs defaultValue={GrowthMedium.CASO}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value={GrowthMedium.CASO}>
              {t(GrowthMedium.CASO)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.MALT}>
              {t(GrowthMedium.MALT)}
            </TabsTrigger>
            <TabsTrigger value={GrowthMedium.DG18} className="uppercase">
              {t(GrowthMedium.DG18)}
            </TabsTrigger>
          </TabsList>
          <div className="p-2">
            <TabsContent value={GrowthMedium.CASO}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.CASO}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.MALT}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.MALT}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
            <TabsContent value={GrowthMedium.DG18}>
              <SurfaceMicrobialSampleSetSampleFields
                growthMedium={GrowthMedium.DG18}
                set={request}
                onSetChange={setRequest}
                error={error}
                disabled={immutable}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function DustDensitySampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useGetDustDensitySample(id!);

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.DustDensity)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <DustDensitySampleForm sample={data!} immutable={immutable} />;
}

function DustDensitySampleForm({
  sample,
  immutable,
}: {
  sample: DustDensitySample;
  immutable: boolean;
}) {
  const [request, setRequest] = useState(sample);
  const mutation = usePutDustDensitySample();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    sample,
    {
      resetOnError: false,
      toastError: true,
    },
  );

  // needed to enable switching between different sample sets / assessments
  useEffect(() => {
    if (sample.id !== request.id) {
      setRequest(sample);
    }
  }, [request.id, sample]);

  return (
    <div className="h-full">
      <H4>{t(Kind.DustDensity)}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={request.number}
            onNumberChange={(number) =>
              setRequest(updateSampleNumber(request, number))
            }
            error={error}
            disabled={immutable}
          />
          <CreatedAtInput
            createdAt={request.createdAt}
            onCreatedAtChange={(createdAt) =>
              setRequest(updateSampleCreatedAt(request, createdAt))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInput
          label={t("Ort")}
          field="location"
          text={request.location}
          onTextChange={(location) =>
            setRequest(updateLocation(request, location))
          }
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInput
          label={t("Stelle")}
          field="spot"
          text={request.spot}
          onTextChange={(spot) => setRequest(updateSpot(request, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <LaboratorySelect
          labId={request.labId}
          onLabIdChange={(labId) => setRequest(updateSampleLab(request, labId))}
          error={error}
          disabled={immutable}
        />
        <CommentInput
          comment={request.comment}
          onCommentChange={(comment) =>
            setRequest(updateSampleComment(request, comment))
          }
          error={error}
          disabled={immutable}
        />
      </div>
    </div>
  );
}

function WaterSampleFormLoader({
  id,
  immutable,
}: {
  id: EntityId;
  immutable: boolean;
}) {
  const { data, isLoading, error } = useGetWaterSample(id!);

  if (isLoading) {
    return (
      <div className="h-full">
        <H4>{t(Kind.Water)}</H4>
        <Separator className="mt-1 mb-4" />
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return <WaterSampleForm sample={data!} immutable={immutable} />;
}

function WaterSampleForm({
  sample,
  immutable,
}: {
  sample: WaterSample;
  immutable: boolean;
}) {
  const [request, setRequest] = useState(sample);
  const mutation = usePutWaterSample();
  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    sample,
    {
      resetOnError: false,
      toastError: true,
    },
  );

  // needed to enable switching between different sample sets / assessments
  useEffect(() => {
    if (sample.id !== request.id) {
      setRequest(sample);
    }
  }, [request.id, sample]);

  return (
    <div className="h-full">
      <H4>
        {t(Kind.Water)}
        {request.waterKind === "warm" ? t(" - Warmwasser") : t(" - Kaltwasser")}
      </H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            number={request.number}
            onNumberChange={(number) =>
              setRequest(updateSampleNumber(request, number))
            }
            error={error}
            disabled={immutable}
          />
          <CreatedAtInput
            createdAt={request.createdAt}
            onCreatedAtChange={(createdAt) =>
              setRequest(updateSampleCreatedAt(request, createdAt))
            }
            error={error}
            disabled={immutable}
          />
        </div>
        <TextInput
          label={t("Ort")}
          field="location"
          text={request.location}
          onTextChange={(location) =>
            setRequest(updateLocation(request, location))
          }
          errors={inputValidationErrorMessages(error, "location")}
          disabled={immutable}
        />
        <TextInput
          label={t("Stelle")}
          field="spot"
          text={request.spot}
          onTextChange={(spot) => setRequest(updateSpot(request, spot))}
          errors={inputValidationErrorMessages(error, "spot")}
          disabled={immutable}
        />
        <LaboratorySelect
          labId={request.labId}
          onLabIdChange={(labId) => setRequest(updateSampleLab(request, labId))}
          error={error}
          disabled={immutable}
        />
        <div className="grid grid-cols-2 gap-4">
          <TemperatureInput
            temperature={request.temperature}
            onTemperatureChange={(temperature) =>
              setRequest(updateTemperature(request, temperature))
            }
            error={error}
            disabled={immutable}
          />
          <BooleanSelect
            label={t("Probenentnahme desinfiziert")}
            field="desinfected"
            value={request.desinfected}
            onValueChange={(desinfected) =>
              setRequest({ ...request, desinfected })
            }
            errors={inputValidationErrorMessages(error, "desinfected")}
          />
        </div>
        <CommentInput
          comment={request.comment}
          onCommentChange={(comment) =>
            setRequest(updateSampleComment(request, comment))
          }
          error={error}
          disabled={immutable}
        />
      </div>
    </div>
  );
}

function NumberInput({
  number,
  onNumberChange,
  error,
  disabled,
}: {
  number: string;
  onNumberChange: (number: string) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Probennummer")}</Label>
      <Input
        type="text"
        value={number}
        onChange={(e) => onNumberChange(e.target.value)}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="number" />
    </div>
  );
}

function TemperatureInput({
  temperature,
  onTemperatureChange,
  error,
  disabled,
}: {
  temperature: number;
  onTemperatureChange: (temperature: number) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Temperatur")}</Label>
      <Input
        type="number"
        value={temperature}
        onChange={(e) => onTemperatureChange(Number(e.target.value))}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="temperature" />
    </div>
  );
}

function LaboratorySelect({
  labId,
  onLabIdChange,
  error,
  disabled,
}: {
  labId: EntityId;
  onLabIdChange: (labId: EntityId) => void;
  error: unknown;
  disabled?: boolean;
}) {
  const {
    data: laboratories,
    isLoading,
    error: loadingError,
  } = useGetLaboratories();

  if (isLoading) {
    return (
      <div className="grid content-start gap-1.5">
        <Label>{t("Labor")}</Label>
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (loadingError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Labor")}</Label>
      <Select
        value={String(labId)}
        onValueChange={onLabIdChange}
        disabled={disabled ?? false}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Labor auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {laboratories!.map((lab) => (
            <SelectItem key={lab.id} value={lab.id}>
              {lab.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputValidationErrors error={error} field="labId" />
    </div>
  );
}

function AirMicrobialSampleSetSampleFields({
  growthMedium,
  set,
  onSetChange,
  error,
  disabled,
}: {
  growthMedium: GrowthMedium;
  set: AirMicrobialSampleSet;
  onSetChange: (set: AirMicrobialSampleSet) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={airMicrobialSampleIsActivated(set, growthMedium)}
          onCheckedChange={(state: CheckedState) => {
            switch (state) {
              case true: {
                onSetChange(activateAirMicrobialSetSample(set, growthMedium));
                break;
              }
              case false: {
                onSetChange(deactivateAirMicrobialSetSample(set, growthMedium));
                break;
              }
              default:
                break;
            }
          }}
          disabled={disabled}
        />
        <Label htmlFor="terms">{`Hat ${t(growthMedium)} Probe`}</Label>
      </div>
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.CASO && (
          <AirMicrobialSampleFields
            sample={set.caso!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetCaso(set, sample))
            }
            error={error}
          />
        )}
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.MALT && (
          <AirMicrobialSampleFields
            sample={set.malt!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetMalt(set, sample))
            }
            error={error}
          />
        )}
      {airMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.DG18 && (
          <AirMicrobialSampleFields
            sample={set.dg18!}
            onSampleChange={(sample) =>
              onSetChange(updateAirMicrobialSetDg18(set, sample))
            }
            error={error}
          />
        )}
    </div>
  );
}

function AirMicrobialSampleFields({
  sample,
  onSampleChange,
  error,
  disabled,
}: {
  sample: AirMicrobialSample;
  onSampleChange: (sample: AirMicrobialSample) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <BaseSampleFields
        sample={sample}
        onSampleChange={onSampleChange}
        error={error}
        disableSetBoundFields
        disabled={disabled}
      />
      {/*
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1">
          <SamplingVolumeInput
            volume={sample.samplingVolume}
            onVolumeChange={(volume) =>
              onSampleChange(updateSamplingVolume(sample, volume))
            }
            error={error}
            disabled={disabled}
          />
        </div>
        <div className="col-span-2">
          <DeviceSelect
            deviceId={sample.deviceId}
            onDeviceIdChange={(deviceId) =>
              onSampleChange(updateDeviceId(sample, deviceId))
            }
            error={error}
            disabled={disabled}
          />
        </div>
      </div>
      */}
    </div>
  );
}

function SamplingVolumeInput({
  volume,
  onVolumeChange,
  error,
  disabled,
}: {
  volume: number;
  onVolumeChange: (volume: number) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Ansaugvolumen")}</Label>
      <Input
        type="number"
        value={volume}
        onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
        disabled={disabled}
      />
      <InputValidationErrors error={error} field="samplingVolume" />
    </div>
  );
}

function DeviceSelect({
  deviceId,
  onDeviceIdChange,
  error,
  disabled,
}: {
  deviceId: EntityId;
  onDeviceIdChange: (deviceId: EntityId) => void;
  error: unknown;
  disabled?: boolean;
}) {
  const { data: devices, isLoading, error: loadingError } = useGetDevices();

  if (isLoading) {
    return (
      <div className="grid content-start gap-1.5">
        <Label>{t("Verwendetes Gerät")}</Label>
        <div>
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      </div>
    );
  }

  if (loadingError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Verwendetes Gerät")}</Label>
      <Select
        value={String(deviceId)}
        onValueChange={onDeviceIdChange}
        disabled={disabled ?? false}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Gerät auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {devices!.map((device) => (
            <SelectItem key={device.id} value={device.id}>
              {device.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <InputValidationErrors error={error} field="deviceId" />
    </div>
  );
}

function SurfaceMicrobialSampleSetSampleFields({
  growthMedium,
  set,
  onSetChange,
  error,
  disabled,
}: {
  growthMedium: GrowthMedium;
  set: SurfaceMicrobialSampleSet;
  onSetChange: (set: SurfaceMicrobialSampleSet) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="terms"
          checked={surfaceMicrobialSampleIsActivated(set, growthMedium)}
          onCheckedChange={(state: CheckedState) => {
            switch (state) {
              case true: {
                onSetChange(
                  activateSurfaceMicrobialSetSample(set, growthMedium),
                );
                break;
              }
              case false: {
                onSetChange(
                  deactivateSurfaceMicrobialSetSample(set, growthMedium),
                );
                break;
              }
              default:
                break;
            }
          }}
          disabled={disabled}
        />
        <Label htmlFor="terms">{`Hat ${t(growthMedium)} Probe`}</Label>
      </div>
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.CASO && (
          <SurfaceMicrobialSampleFields
            sample={set.caso!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetCaso(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.MALT && (
          <SurfaceMicrobialSampleFields
            sample={set.malt!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetMalt(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
      {surfaceMicrobialSampleIsActivated(set, growthMedium) &&
        growthMedium === GrowthMedium.DG18 && (
          <SurfaceMicrobialSampleFields
            sample={set.dg18!}
            onSampleChange={(sample) =>
              onSetChange(updateSurfaceMicrobialSetDg18(set, sample))
            }
            error={error}
            disabled={disabled}
          />
        )}
    </div>
  );
}

function SurfaceMicrobialSampleFields({
  sample,
  onSampleChange,
  error,
  disabled,
}: {
  sample: SurfaceMicrobialSample;
  onSampleChange: (sample: SurfaceMicrobialSample) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <BaseSampleFields
        sample={sample}
        onSampleChange={onSampleChange}
        error={error}
        disableSetBoundFields
        disabled={disabled}
      />
    </div>
  );
}

function BaseSampleFields<S extends Sample>({
  sample,
  onSampleChange,
  error,
  disableSetBoundFields,
  disabled,
}: {
  sample: S;
  onSampleChange: (sample: S) => void;
  error: unknown;
  disableSetBoundFields?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          number={sample.number}
          onNumberChange={(number) =>
            onSampleChange(updateSampleNumber(sample, number))
          }
          error={error}
          disabled={disableSetBoundFields || disabled}
        />
        <CreatedAtInput
          createdAt={sample.createdAt}
          onCreatedAtChange={(createdAt) =>
            onSampleChange(updateSampleCreatedAt(sample, createdAt))
          }
          error={error}
          disabled
        />
      </div>
      <LaboratorySelect
        labId={sample.labId}
        onLabIdChange={(labId) =>
          onSampleChange(updateSampleLab(sample, labId))
        }
        error={error}
        disabled={disableSetBoundFields || disabled}
      />
      <CommentInput
        comment={sample.comment}
        onCommentChange={(comment) =>
          onSampleChange(updateSampleComment(sample, comment))
        }
        error={error}
        disabled={disabled}
      />
    </div>
  );
}

function CommentInput({
  comment,
  onCommentChange,
  error,
  disabled,
}: {
  comment: string;
  onCommentChange: (comment: string) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Kommentar (für Labor)")}</Label>
      <Textarea
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="comment" />
    </div>
  );
}

function CreatedAtInput({
  createdAt,
  onCreatedAtChange,
  error,
  disabled,
}: {
  createdAt: string | null;
  onCreatedAtChange: (createdAt: string | null) => void;
  error: unknown;
  disabled?: boolean;
}) {
  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Erstellt am")}</Label>
      <DateStringPicker
        date={createdAt}
        onDateChange={onCreatedAtChange}
        disabled={disabled ?? false}
      />
      <InputValidationErrors error={error} field="createdAt" />
    </div>
  );
}
